/* eslint-disable no-shadow */

import { difference, isEqual, uniq } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import Card from "../services/Card";

interface Service {
	open: Card.Open;
	close: Card.Close;
}

/** A gone session (reconnect) has nothing to close: not an error. */
const onCardError = (error: unknown) => {
	if ((error as Error)?.message === Card.SESSION_NOT_FOUND) return;
	console.error("Error catched when close card:", error);
};

function useKeepCardsOpen(ids: number[], Service: Service) {
	const uniqIds = useMemo(() => uniq(ids), [ids]);

	const prevIdsRef = useRef<number[]>([]);

	useEffect(() => {
		if (isEqual(uniqIds, prevIdsRef.current)) return;

		const prevIds = prevIdsRef.current;

		prevIdsRef.current = uniqIds;

		const idsForClose = difference(prevIds, uniqIds);
		const idsForOpen = difference(uniqIds, prevIds);

		if (idsForClose.length) Service.close(idsForClose).catch(onCardError);

		idsForOpen.forEach((id) => Service.open(id).catch(onCardError));
	}, [Service, ids, uniqIds]);

	useEffect(
		() => () => {
			if (prevIdsRef.current.length)
				Service.close(prevIdsRef.current).catch(onCardError);
		},
		[Service],
	);
}

export default useKeepCardsOpen;
